import axios from 'axios';
import { now } from 'lodash';
import moment from 'moment';
import store from '@/store';

import api from '@/services/api';

import { getUrlContext } from '@/helpers/AppHelpers';
import { PageMapping } from '@/helpers/PageMappingHelpers';
import { formatNombreEnMonetaire } from '@/helpers/StringHelpers';

import i18n from '@/js/i18n';

const CATEGORIE_PROMO = {
  GENERIQUE: 'GENERIC',
  DEFAUT: 'DEFAULT',
  EXPIRE: 'EXPIRED',
};

/**
 * @typedef Promo
 * @property {string} campaignName Nom de la campagne de promotion (e.g. "WO - Sports")
 * @property {number} id
 * @property {string} promoCode Code de promo - OpenBet (e.g. "A30PS10625")
 */

/**
 * @typedef ReponseInfoPromo
 * @property {Record<symbol | string | number, unknown>[]} actions
 * @property {InfoPromo} data
 * @property {Record<symbol | string | number, unknown>[]} errors
 * @property {Record<symbol | string | number, unknown>} player
 */

/**
 * @typedef InfoPromo
 * @property {string[]} categories
 * @property {string[]} channels
 * @property {string} description
 * @property {string|null} endDate
 * @property {string|null} entryExpiryDate
 * @property {string} name
 * @property {number} promotionRef
 * @property {InfoPromoReward[]} reward
 * @property {string} startDate
 * @property {string|null} url
 */

/**
 * @typedef InfoPromoReward
 * @property {number} amount
 * @property {number} maxAmount
 * @property {number} percentage
 * @property {string} type
 */

/**
 * @typedef DocumentPromo
 * @property {string} idob Identificateur de la promo - OpenBet (e.g. "A30PS10625")
 * @property {string} texte
 * @property {string} titre
 */

export default {
  /**
   * Réclamer une promo via un promo-code
   * @param {string} codePromo
   * @returns
   * success : si tous les services ont trouver la promo --> optin avec promo
   * erreur:
   *    error.url : oups si un des services validerPromo et obtenirPromo retourne un 500 ou 401
   *    error : objet vide pour pouvoir continuer vers la page optin sans promo
   */
  async reclamerPromo(codePromo) {
    return this.recherchePromo(codePromo).catch((error) => {
      if (error == CATEGORIE_PROMO.EXPIRE) {
        return this.reclamerPromoGenerique(error);
      }
      throw error;
    });
  },

  /**
   * Recherche Promo spécifique
   * @param {string} codePromo
   * @returns
   */
  async recherchePromo(codePromo) {
    const valPromo = await this.validerPromo(codePromo);
    const infoPromo = await this.obtenirPromo(valPromo);
    const promo = await this.getInfoAppPromo(
      infoPromo.data.categories[0],
      valPromo?.promoCode
    ).catch((error) => {
      if (!this.extraireMontant(infoPromo)) {
        throw error; // generique
      }
      return this.getInfoAppPromo(CATEGORIE_PROMO.DEFAUT);
    });
    promo.idob = codePromo;
    return this.sauvegarderInfoPromo(promo, infoPromo);
  },

  /**
   * Valider un code promo
   * @param {string} codePromo
   * @returns {Promise<Promo>}
   */
  async validerPromo(codePromo) {
    return new Promise((resolve, reject) => {
      api
        .call(
          `promo-api/promotions-service/api/v0/registrationPromocode/${codePromo}`,
          `promo/validerPromo-${codePromo}`,
          'GET'
        )
        .then(
          (response) => {
            const resp = response.data;
            if (resp.data.length) {
              resolve(resp.data[0]);
            } else {
              reject(CATEGORIE_PROMO.EXPIRE);
            }
          },
          (error) => {
            // url de retour est mis dans la methode call si nous avons http 500 / 401
            reject(error);
          }
        );
    });
  },
  /**
   * Obtenir les informations de la promo à partir du reference id
   * @param {Promo} valPromo
   * @returns {Promise<ReponseInfoPromo>}
   */
  async obtenirPromo(valPromo) {
    return new Promise((resolve, reject) => {
      const ref = valPromo.id;
      api
        .call(
          `promo-api/promotions-service/api/v0/promotions/${ref}`,
          `promo/obtenirPromo-${ref}`,
          'GET'
        )
        .then(
          (response) => {
            // si !CAT && montant = 0 ==> optin sans promo
            /** @type {ReponseInfoPromo} */
            const aData = response.data;
            const montant = this.extraireMontant(aData);
            if (montant) {
              // Si nous n'avons pas de categories ou nous plusieurs categorie == > DEFAUT
              if (!aData.data.categories || aData.data.categories.length !== 1) {
                aData.data.categories = [CATEGORIE_PROMO.DEFAUT];
              }
            } else if (aData.data.categories.length === 0) {
              reject({ url: PageMapping.ERREUR_GENERIQUE.siteUrl });
            }
            resolve(aData);
          },
          (error) => {
            const errData = error.response.data;
            if (errData.errors[0] && errData.errors[0].type === 'OFFER_INACTIVE') {
              reject(CATEGORIE_PROMO.EXPIRE);
            } else {
              // url de retour est mis dans la methode call si nous avons http 500 / 401
              reject(error);
            }
          }
        );
    });
  },
  /**
   * Obtenir les informations de la famille de promo à partir d'une catégorie-Id
   * @param {string} category
   * @param {string} codePromo
   * @returns {Promise<DocumentPromo>}
   */
  async getInfoAppPromo(category = CATEGORIE_PROMO.GENERIQUE, codePromo) {
    return new Promise((resolve, reject) => {
      axios
        .get(
          getUrlContext(
            `${process.env.VUE_APP_REST_JEL.replace(
              'CONTENT_APP',
              'promo'
            )}/${category.toUpperCase()}`
          ),
          {
            params: { lang: i18n.locale, t: moment().unix() },
          }
        )
        .then((response) => {
          if (codePromo && response.data.name === 'ACQUISITION') {
            const promoAcquisition = response.data['@nodes'].find(
              (promo) => codePromo === response.data[promo].idob
            );
            resolve(response.data[promoAcquisition]);
          } else {
            resolve(response.data); // Notifie la fonction que l'appel http est complété.
          }
        })
        .catch((error) => {
          // L'appel http a échoué, on notifie la fonction.
          reject(error);
        });
    });
  },

  /**
   * Réclamer Promo générique
   * @param {string} category
   * @returns
   */
  async reclamerPromoGenerique(category) {
    // eslint-disable-next-line no-useless-catch
    try {
      const promo = await this.getInfoAppPromo(category);
      const valPromo = await this.validerPromo(promo.idob);
      await this.obtenirPromo(valPromo).then((infoPromo) => {
        console.log(`Résultat final : ${promo}`);
        this.sauvegarderInfoPromo(promo, infoPromo);
      });
    } catch (error) {
      throw error;
    }
  },
  /**
   * Sauvegarde de la promo dans le store
   * @param {DocumentPromo} promo
   * @param {ReponseInfoPromo} infoPromo
   * @returns {Promise<DocumentPromo>}
   */
  async sauvegarderInfoPromo(promo, infoPromo) {
    const montant = this.extraireMontant(infoPromo);
    const montantFormatte = formatNombreEnMonetaire(montant, 2);
    const pattern = /{{valeur}}/;
    promo.titre = promo.titre.replace(new RegExp(pattern, 'g'), montantFormatte);
    promo.texte = promo.texte.replace(new RegExp(pattern, 'gm'), montantFormatte);
    store.commit('promo/SET_MONTANT_PROMO', montant);
    store.commit('promo/SET_PROMO', promo);
    store.commit('compte/SET_CODE_PROMO', promo.idob);
    return promo;
  },

  /**
   * Extraire le montant total de la promo si elle existe
   * @param {ReponseInfoPromo} infoPromo
   * @returns {number}
   */
  extraireMontant(infoPromo) {
    try {
      // Somme de la quantité (amount) pour tous les rewards de la promo
      return infoPromo.data.reward.reduce((total, reward) => total + reward.amount, 0);
    } catch (error) {
      return 0;
    }
  },

  /**
   * Obtenir le solde total des active rewards de type Sportsbook (Freebet)
   * @returns {Promise<number>}
   */
  async obtenirSoldeActiveRewards() {
    try {
      const response = await api.call(
        'ce-portal/portal_api/promotions/tokens/active-rewards',
        'promo/active-rewards',
        'GET',
        { withCredentials: true, params: { t: Date.now() } }
      );
      const rewards = response.data;
      if (!rewards) {
        return 0;
      }
      // Somme des current_balance pour les rewards de type Freebet (Sportsbook tokens) uniquement
      // Pour sommer tous les types (GP et Freebet):
      return rewards
        .filter((reward) => reward.type === 'Freebet')
        .reduce((total, reward) => total + parseFloat(reward.current_balance), 0);
    } catch (error) {
      return 0;
    }
  },
};
